import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ConfirmationMessageModal,
  TextFieldWrapper,
  SelectFieldWrapper,
  TextEditorFieldWrapper,
  FullScreenLoader,
  Modal,
  ModalAlert,
  ToggleSwitch,
  ScrollToTop,
  EmailPreview,
  PrintLayout,
  AutocompleteOptions,
} from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import {
  EmailAdd,
  EmailResponse,
  GlobalEmailResponse,
} from '@Models/emails.models';
import {
  useAddEmailMutation,
  useEditEmailMutation,
  useGetEmailQuery,
} from '@Services/email.service';
import {
  useAddGlobalEmailMutation,
  useEditGlobalEmailMutation,
  useGetGlobalEmailQuery,
} from '@Services/globalEmailTemplates.service';
import { useGetAllGroupsQuery } from '@Services/groups.service';
import { useGetTagsQuery } from '@Services/tags.service';
import { resetUploadData, setUploadData } from '@Slices/upload.slice';
import { AttachmentType } from '@Typings/emailUpload.types';
import {
  BaseAlertProps,
  CommonApiError,
  ConfirmationActionTypes,
  DialerCrudTypes,
} from '@Typings/global.types';
import {
  getConfirmationMessageNode,
  SelectOptions,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';
import { useAppDispatch, useAppSelector } from '@Utils/hooks/useStore.hook';

import EmailFileAttachment from '../EmailFileAttachment';
import EmailHeaderFooter from '../EmailHeaderFooter';

import { EmailsModalInputs, EmailsModalProps } from './EmailsModal.types';
import EmailsModalSchema from './EmailsModalSchema';

const EmailsModal: FunctionComponent<EmailsModalProps> = (
  props: EmailsModalProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint } = usePrint();

  const {
    emailId,
    setEmailId,
    crudType,
    setCrudType,
    unarchiveBtnOnClick,
    deleteBtnOnClick,
    isAdmin,
  } = props;

  /** STATES */
  const [defaultType, setDefaultType] = useState<boolean>(false);
  const [previousCrudType, setPreviousCrudType] =
    useState<DialerCrudTypes | null>(crudType);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [modalAlertState, setModalAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [previewData, setPreviewData] = useState<EmailsModalInputs | null>(
    null
  );
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  /** This state helps to prevent error message from showing up when user is editing default email,
   * even if user has not performed any changes
   * This happens because of previous error which was cached by RTK
   * This is a fix until RTK Mutation supports resetting cache data */
  const [showDefaultEmailEditError, setShowDefaultEmailEditError] =
    useState<boolean>(false);

  /** FORM METHODS */
  const methods = useForm<EmailsModalInputs>({
    resolver: yupResolver(EmailsModalSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  /** REDUX STATE */
  const uploadState = useAppSelector((state) => state.emailUpload);
  const { header, footer, listOfAttachments } = uploadState;
  const dispatch = useAppDispatch();

  /** RTK QUERY HOOKS */
  const {
    data: emailData,
    isLoading: emailIsLoading,
    isFetching: emailIsFetching,
    error: getEmailsError,
  } = useGetEmailQuery(emailId, {
    skip: isAdmin || !emailId || !crudType,
  });

  const {
    data: globalEmailData,
    isLoading: globalEmailIsLoading,
    isFetching: globalEmailIsFetching,
    error: getGlobalEmailError,
  } = useGetGlobalEmailQuery(emailId, {
    skip: !isAdmin || !emailId || !crudType,
  });

  const {
    data: allGroups,
    isLoading: allGroupsIsLoading,
    isFetching: allGroupsIsFetching,
  } = useGetAllGroupsQuery();

  const [addEmail, addEmailMethods] = useAddEmailMutation();
  const [editEmail, editEmailMethods] = useEditEmailMutation();
  const [addGlobalEmail, addGlobalEmailMethods] = useAddGlobalEmailMutation();
  const [editGlobalEmail, editGlobalEmailMethods] =
    useEditGlobalEmailMutation();
  const { data: placeholderTags } = useGetTagsQuery();

  /** LOADING STATE */
  const loading =
    emailIsLoading ||
    emailIsFetching ||
    globalEmailIsLoading ||
    globalEmailIsFetching ||
    allGroupsIsLoading ||
    allGroupsIsFetching ||
    addEmailMethods.isLoading ||
    editEmailMethods.isLoading ||
    addGlobalEmailMethods.isLoading ||
    editGlobalEmailMethods.isLoading;

  const data: EmailResponse | GlobalEmailResponse | undefined =
    emailData || globalEmailData;

  const emailError = getEmailsError || getGlobalEmailError;

  const isUploading =
    header.isLoading || footer.isLoading || listOfAttachments.isLoading;

  /** ReadOnly prop for form fields if CRUD Type is View or Archive View */
  const readOnly =
    crudType === DialerCrudTypes.View || crudType === DialerCrudTypes.Archive;

  /** Select options */
  const options = SelectOptions();

  const groupOptions: AutocompleteOptions[] =
    allGroups?.map((group) => ({
      label: group.name,
      value: group.id,
    })) || [];

  /** Returns Modal Header Text */
  const getHeaderText = (): string | undefined => {
    if (loading) {
      return '';
    }
    if (crudType === DialerCrudTypes.Preview) {
      return `Preview ${previewData?.name || ''}`;
    }

    return crudType === DialerCrudTypes.Add ? t('Email.New Email') : data?.name;
  };

  /**
   * Closes modal by setting `crudType` state to null
   *
   * Resetting `previousCrudType` state
   *
   * Resets `emailId` state
   *
   * Removes modalAlert after modal close by setting `openAlert` state to false
   *
   * Resets error for default data edit by setting `showDefaultEmailEditError` state to false
   *
   * Resets form state
   */
  const handleClose = useCallback(() => {
    setPreviousCrudType(null);
    setCrudType(null);
    setEmailId('');
    setPreviewData(null);
    setModalAlertState({ openAlert: false });
    setShowDefaultEmailEditError(false);
    methods.unregister();
  }, [methods, setCrudType, setEmailId]);

  /**
   * View CRUD - Resets Form Fields with the API DATA
   * Add CRUD - Empty the Form Fields
   */
  useEffect(() => {
    if (previewData && previousCrudType === DialerCrudTypes.Preview) {
      methods.reset({
        name: previewData.name,
        subject: previewData.subject,
        message: previewData.message,
        type: previewData.type,
        group: {
          label: previewData.group?.label,
          value: previewData.group?.value,
        },
      });
    } else if (
      data &&
      (crudType === DialerCrudTypes.View ||
        crudType === DialerCrudTypes.Edit ||
        crudType === DialerCrudTypes.Archive) &&
      previousCrudType === null
    ) {
      setDefaultType(isAdmin ? false : (data as EmailResponse).default);
      methods.reset({
        name: data.name,
        type: {
          label: data.type,
          value: data.type,
        },
        subject: data.subject,
        message: data.message,
        group: {
          label: (data as EmailResponse)?.group?.name,
          value: (data as EmailResponse)?.group?.id,
        },
      });
      dispatch(
        setUploadData({
          storeKey: AttachmentType.Header,
          attachment: {
            isLoading: false,
            data: data.headerUrl
              ? [
                  {
                    url: data.headerUrl,
                    filename: 'headerImg',
                    bytes: 0,
                  },
                ]
              : [],
          },
        })
      );
      dispatch(
        setUploadData({
          storeKey: AttachmentType.Footer,
          attachment: {
            isLoading: false,
            data: data.footerUrl
              ? [
                  {
                    url: data.footerUrl,
                    filename: 'footerImg',
                    bytes: 0,
                  },
                ]
              : [],
          },
        })
      );
      dispatch(
        setUploadData({
          storeKey: AttachmentType.Attachment,
          attachment: {
            isLoading: false,
            data: data.attachments,
          },
        })
      );
    } else if (
      crudType === DialerCrudTypes.Add &&
      previousCrudType !== DialerCrudTypes.Preview
    ) {
      setDefaultType(false);
      methods.reset({
        name: '',
        type: {
          label: undefined,
          value: undefined,
        },
        subject: '',
        message: '',
        group: {
          label: undefined,
          value: undefined,
        },
      });
      dispatch(resetUploadData({ storeKey: null }));
    }
  }, [
    methods,
    previewData,
    data,
    crudType,
    dispatch,
    previousCrudType,
    isAdmin,
  ]);

  /** SUCCESS AND ERROR ALERT HANDLERS --> */
  useEffect(() => {
    // SHOW ALERT FOR GET EMAIL ERROR
    if (getEmailsError) {
      handleClose();
      triggerAlert(getEmailsError, setAlertState);
    }
  }, [getEmailsError, handleClose, triggerAlert]);

  useEffect(() => {
    // ADD EMAIL ERROR
    if (addEmailMethods.error) {
      if ((addEmailMethods.error as FetchBaseQueryError).status === 400) {
        methods.setValue('name', '');
      }
      triggerAlert(addEmailMethods.error, setModalAlertState);
    }

    if (addGlobalEmailMethods.error) {
      triggerAlert(addGlobalEmailMethods.error, setModalAlertState);
    }

    // ADD EMAIL SUCCESS
    if (addEmailMethods.isSuccess || addGlobalEmailMethods.isSuccess) {
      // Trigger Success Alert according to Response `alert` key
      switch (addEmailMethods.data?.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.AUTO_MARK_DEFAULT:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.AUTO_ADD_DEFAULT_EMAIL,
            setAlertState
          );
          break;
        case CommonConstants.GLOBAL_SUCCESS_CODES.SWAP_DEFAULT_RESOURCE:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_EMAIL_UPDATED,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.ADD_EMAIL,
            setAlertState
          );
      }

      handleClose();
    }
  }, [
    methods,
    addEmailMethods.data,
    addEmailMethods.isSuccess,
    addEmailMethods.error,
    addGlobalEmailMethods.isSuccess,
    addGlobalEmailMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // EDIT EMAIL ERROR
    if (editEmailMethods.error) {
      setShowDefaultEmailEditError(true);
      if (
        ((editEmailMethods.error as FetchBaseQueryError).data as CommonApiError)
          .message !== CommonConstants.GLOBAL_ERROR_CODES.RESOURCE_IS_A_DEFAULT
      ) {
        triggerAlert(editEmailMethods.error, setModalAlertState);
      }
    }

    if (editGlobalEmailMethods.error) {
      triggerAlert(editGlobalEmailMethods.error, setModalAlertState);
    }

    // EDIT EMAIL SUCCESS
    if (editEmailMethods.isSuccess || editGlobalEmailMethods.isSuccess) {
      // Trigger Success Alert according to Response `alert` key
      switch (editEmailMethods.data?.alert) {
        case CommonConstants.GLOBAL_SUCCESS_CODES.SWAP_DEFAULT_RESOURCE:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.DEFAULT_EMAIL_UPDATED,
            setAlertState
          );
          break;
        default:
          triggerAlert(
            CommonConstants.GLOBAL_MESSAGES.EDIT_EMAIL,
            setAlertState
          );
      }

      handleClose();
    }
  }, [
    editEmailMethods.data?.alert,
    editEmailMethods.isSuccess,
    editEmailMethods.error,
    editGlobalEmailMethods.isSuccess,
    editGlobalEmailMethods.error,
    handleClose,
    triggerAlert,
  ]);

  useEffect(() => {
    // Trigger Error Alert according to Response `error` message
    // For RESOURCE_IS_A_DEFAULT MESSAGE
    if (
      editEmailMethods.error &&
      showDefaultEmailEditError &&
      ((editEmailMethods.error as FetchBaseQueryError).data as CommonApiError)
        .message === CommonConstants.GLOBAL_ERROR_CODES.RESOURCE_IS_A_DEFAULT
    ) {
      triggerAlert(
        t(
          `alertMessages.${CommonConstants.GLOBAL_MESSAGES.EMAIL_IS_A_DEFAULT}`,
          { listType: data?.type }
        ),
        setModalAlertState,
        'error',
        true
      );
    }
  }, [
    showDefaultEmailEditError,
    data?.type,
    editEmailMethods.error,
    triggerAlert,
    t,
  ]);

  useEffect(() => {
    if (emailError) {
      triggerAlert(emailError, setAlertState);
    }
  }, [emailError, triggerAlert]);

  /** <-- SUCCESS AND ERROR ALERT HANDLERS */

  /** FORM HANDLERS --> */
  /** Handles default email toggle state change */
  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => setDefaultType(checked);

  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<EmailsModalInputs> = (formData) => {
    const userIdParam =
      query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';

    const newData: EmailAdd = {
      name: formData.name,
      type: formData.type.value,
      displayType: formData.type.value,
      message: formData.message,
      subject: formData.subject,
      headerUrl: header.data[0]?.url || '',
      footerUrl: footer.data[0]?.url || '',
      attachments: listOfAttachments.data,
      default: defaultType,
      groupId: formData?.group?.value,
    };

    if (data && crudType === DialerCrudTypes.Edit) {
      return isAdmin
        ? editGlobalEmail({ ...data, ...newData })
        : editEmail({ ...data, ...newData });
    }
    if (crudType === DialerCrudTypes.Add) {
      return isAdmin
        ? addGlobalEmail(newData)
        : addEmail({ ...newData, userId: `${userIdParam}` });
    }
  };

  /** Handles preview display of email */
  const handlePreview = () => {
    setPreviewData({
      subject: methods.getValues('subject'),
      message: methods.getValues('message'),
      name: methods.getValues('name'),
      type: methods.getValues('type'),
      group: methods.getValues('group'),
    });
    setPreviousCrudType(crudType);
    setCrudType(DialerCrudTypes.Preview);
  };

  /** Handles save functionality of preview modal */
  const onPreviewSave = () => {
    const userIdParam =
      query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const newData: EmailAdd = {
      name: previewData?.name || '',
      type: previewData?.type.value || '',
      displayType: previewData?.type.value || '',
      subject: previewData?.subject || '',
      message: previewData?.message || '',
      headerUrl: header.data[0]?.url || '',
      footerUrl: footer.data[0]?.url || '',
      attachments: listOfAttachments.data,
      default: defaultType,
      ...(userIdParam ? { userId: `${userIdParam}` } : {}),
    };
    if (previousCrudType === DialerCrudTypes.Add) {
      return isAdmin ? addGlobalEmail(newData) : addEmail(newData);
    }
    if (data && previousCrudType === DialerCrudTypes.Edit) {
      return isAdmin
        ? editGlobalEmail({ ...data, ...newData })
        : editEmail({ ...data, ...newData });
    }
  };

  /** Handles back functionality of preview modal */
  const handleBackClick = () => {
    setCrudType(previousCrudType);
    setPreviousCrudType(DialerCrudTypes.Preview);
  };
  /** <-- FORM HANDLERS */

  /** Button Group Component
   *
   * Returns Actions Button according to the CRUD Type.
   */
  const getButtonGroupComponent = (): JSX.Element | null => {
    switch (crudType) {
      case DialerCrudTypes.Add:
        return (
          <>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={
                defaultType && methods.formState.isValid ? 'button' : 'submit'
              }
              variant="contained"
              color="primary"
              onClick={() => {
                if (defaultType && methods.formState.isValid) {
                  setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                }
              }}
              disabled={isUploading}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      case DialerCrudTypes.View:
        return (
          <>
            {!emailData?.global && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={emailData?.default || !methods.formState.isValid}
                  onClick={() => {
                    setIsConfirmationModalOpen(true);
                    setConfirmationActionType(ConfirmationActionTypes.Delete);
                  }}
                >
                  {t('common.messages.Delete')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 'auto', mr: 1.6 }}
                  disabled={!methods.formState.isValid}
                  onClick={() => setCrudType(DialerCrudTypes.Edit)}
                >
                  {t('common.messages.Edit')}
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              color="secondary"
              disabled={!methods.formState.isValid}
              onClick={handlePrint}
            >
              {t('common.messages.Print')}
            </Button>
          </>
        );
      case DialerCrudTypes.Edit:
        return (
          <>
            <Button
              variant="outlined"
              color="error"
              disabled={
                emailData?.default || isUploading || !methods.formState.isValid
              }
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Delete);
              }}
            >
              {t('common.messages.Delete')}
            </Button>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              type={
                defaultType && methods.formState.isValid ? 'button' : 'submit'
              }
              variant="contained"
              color="primary"
              onClick={() => {
                if (defaultType && methods.formState.isValid) {
                  setIsConfirmationModalOpen(true);
                  setConfirmationActionType(ConfirmationActionTypes.Save);
                }
              }}
              disabled={isUploading || !methods.formState.isValid}
            >
              {t('common.messages.Save')}
            </Button>
          </>
        );
      case DialerCrudTypes.Archive:
        return (
          <>
            <Button
              variant="outlined"
              color="error"
              disabled={!methods.formState.isValid}
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Delete);
              }}
            >
              {t('common.messages.Delete')}
            </Button>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleClose}
            >
              {t('common.messages.Cancel')}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={!methods.formState.isValid}
              onClick={() => {
                setIsConfirmationModalOpen(true);
                setConfirmationActionType(ConfirmationActionTypes.Unarchive);
              }}
            >
              {t('common.messages.Unarchive')}
            </Button>
          </>
        );
      case DialerCrudTypes.Preview:
        return (
          <>
            <Button
              variant="text"
              color="secondary"
              sx={{ ml: 'auto', mr: 1.6 }}
              onClick={handleBackClick}
            >
              {t('common.messages.Back')}
            </Button>
            {previousCrudType !== DialerCrudTypes.View && (
              <Button
                variant="contained"
                color="primary"
                onClick={onPreviewSave}
              >
                {t('common.messages.Save')}
              </Button>
            )}
          </>
        );
      default:
        return null;
    }
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (
      confirmationActionType === ConfirmationActionTypes.Delete &&
      deleteBtnOnClick
    ) {
      handleClose();
      return deleteBtnOnClick();
    }
    if (
      confirmationActionType === ConfirmationActionTypes.Unarchive &&
      unarchiveBtnOnClick
    ) {
      handleClose();
      return unarchiveBtnOnClick();
    }
  };

  /** Handles confirmation click of modal when default value is checked */
  const handleDefaultValueConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    onSubmit(methods.getValues());
  };

  return (
    <>
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(
          confirmationActionType,
          emailData?.default,
          methods.getValues('type.label')
        )}
        onClick={
          defaultType &&
          (crudType === DialerCrudTypes.Add ||
            crudType === DialerCrudTypes.Edit)
            ? handleDefaultValueConfirmationModalClick
            : handleConfirmationModalClick
        }
      />
      <Modal
        alertState={alertState}
        headerText={getHeaderText()}
        open={Boolean(crudType)}
        onClose={handleClose}
      >
        <FormProvider {...methods}>
          <Box
            className="email-modal-form"
            component="form"
            role="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            sx={{
              position: 'relative',
              maxHeight: '70vh',
              padding: '3rem 2.4rem 2.4rem',
              overflow: loading ? 'hidden' : 'auto',
            }}
          >
            <ScrollToTop
              className="email-modal-form"
              dependencies={[
                addEmailMethods.isLoading,
                editEmailMethods.isLoading,
              ]}
            />
            <PrintLayout
              ref={componentRef}
              printData={{
                name: methods.getValues('name'),
                listType: methods.getValues('type')?.value,
                isDefault: defaultType,
                subject: methods.getValues('subject'),
                body: methods.getValues('message'),
              }}
            />
            {loading ? (
              <FullScreenLoader
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'common.white',
                  zIndex: 101,
                }}
              />
            ) : (
              <ModalAlert alertState={modalAlertState} />
            )}
            {isAdmin && crudType !== DialerCrudTypes.Preview && (
              <Box display="flex" mb={3.8}>
                <SelectFieldWrapper
                  name="group"
                  options={groupOptions}
                  multiple={false}
                  textFieldProps={{
                    label: `${t('common.table.Group')}`,
                    placeholder: t('common.table.Select Group'),
                    InputProps: {
                      readOnly,
                    },
                  }}
                  sx={{ minWidth: '24.8rem', maxWidth: '24.8rem', mr: 5 }}
                />
              </Box>
            )}
            {crudType !== DialerCrudTypes.Preview && (
              <Box>
                <TextFieldWrapper
                  name="name"
                  variant="outlined"
                  label={`${t('common.messages.Name')} *`}
                  placeholder={t('common.messages.Email Name')}
                  sx={{
                    minWidth: '51.2rem',
                    mt: modalAlertState.openAlert ? 7 : undefined,
                  }}
                  InputProps={{
                    readOnly,
                  }}
                />
                <Box display="flex" mt={3}>
                  <SelectFieldWrapper
                    name="type"
                    options={options}
                    multiple={false}
                    textFieldProps={{
                      label: `${t('common.table.List Type')} *`,
                      placeholder: t('common.table.Select List Type'),
                      InputProps: {
                        readOnly,
                      },
                    }}
                    sx={{ minWidth: '24.8rem', maxWidth: '24.8rem', mr: 5 }}
                  />
                  {!isAdmin && (
                    <Box>
                      <Typography variant="main">
                        {t('Email.Default Email')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <ToggleSwitch
                            checked={defaultType}
                            onChange={readOnly ? () => {} : handleToggleChange}
                          />
                        }
                        label={
                          <Typography variant="main">
                            {t(
                              defaultType
                                ? 'common.messages.Yes'
                                : 'common.messages.No'
                            )}
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                </Box>
                <Box mt={3}>
                  <TextFieldWrapper
                    name="subject"
                    variant="outlined"
                    label={`${t('Email.Subject')} *`}
                    placeholder={t('Email.Email Subject')}
                    sx={{ minWidth: '51.2rem' }}
                    InputProps={{
                      readOnly,
                    }}
                  />
                </Box>
                <Box mt={2.4}>
                  <EmailHeaderFooter
                    images={header.data}
                    uploadBtnText={t('Email.Add Email Header')}
                    uploadBtnIcon="addPhoto"
                    acceptedFormat={CommonConstants.ATTACHMENT.IMAGE}
                    storeKey={AttachmentType.Header}
                    viewOnly={readOnly}
                    setAlertState={setModalAlertState}
                  />
                </Box>
                <Box my={0.8}>
                  <TextEditorFieldWrapper
                    name="message"
                    placeholderOptions={placeholderTags || []}
                    placeholder={`${t('Email.Type your email message')} *`}
                    readOnly={readOnly}
                  />
                </Box>
                <Box mb={2.4}>
                  <EmailHeaderFooter
                    images={footer.data}
                    uploadBtnText={t('Email.Add Email Footer')}
                    uploadBtnIcon="addPhoto"
                    acceptedFormat={CommonConstants.ATTACHMENT.IMAGE}
                    storeKey={AttachmentType.Footer}
                    viewOnly={readOnly}
                    setAlertState={setModalAlertState}
                  />
                </Box>
                <Box mb={2.4}>
                  <Box>
                    <EmailFileAttachment
                      attachments={listOfAttachments.data}
                      uploadBtnText={t('Email.Attach file')}
                      uploadBtnIcon="attachment"
                      acceptedFormat={CommonConstants.ATTACHMENT.FILE}
                      viewOnly={readOnly}
                      emailPreviewBtnProps={{
                        onClick: handlePreview,
                        disabled: isUploading || !methods.formState.isValid,
                      }}
                      setAlertState={setModalAlertState}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {crudType === DialerCrudTypes.Preview && previewData && (
              <EmailPreview
                data={{ ...previewData, header, footer, listOfAttachments }}
              />
            )}
            <Box display="flex" alignItems="center">
              {getButtonGroupComponent()}
            </Box>
          </Box>
        </FormProvider>
      </Modal>
    </>
  );
};

export default EmailsModal;
